/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    string, bool, arrayOf, object,
    shape, func,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import EventDispatcher from '../../state/reducers/EventDispatcher';
import StatusTracking from '../../common/StatusTracking';
import { getCryptedOrderDetailsURL } from '../../utils/crypto';
import configENV from '../../config';
import { getSSRDeviceType } from '../../../../../state/ducks/App/App-Selectors';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { isEmpty } from '../../utils/object';
import getSiteConfig from '../../utils/getSiteConfig';
import { dateMMMMDDYYYY } from '../../utils/dateString';
import { secureProtocol } from '../../utils/secureProtocol';
import brandNames from '../../assets/data/BrandNames';
import { getIsAuthenticatedStatus } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import Helper from '../../state/reducers/Helper';

const {
    PERSIST_KEY,
    FEDEX_ORDERTRACKING_URL,
    UPS_ORDERTRACKING_URL,
    USPS_ORDERTRACKING_URL,
    UDS_ORDERTRACKING_URL,
} = configENV;

class OrderListItem extends React.Component {
    handleViewDetailsClicked = (
        uuid,
        orderNumber,
        paymentStatus,
        status,
        trackingNumber,
        carrier,
    ) => {
        const {
            match, fetchOrderDetails, history, featureFlags, ssrDeviceType, isAuthenticatedStatus,
        } = this.props;
        // If guest user clicked on the view details button, call fetchOrderDetails to update the order details page
        if (uuid && match.params && match.params.uuid === uuid) {
            fetchOrderDetails(uuid, orderNumber, null, null, {
                update: true,
                paymentStatus,
                status,
                trackingNumber,
                carrier,
            },
            featureFlags,
            ssrDeviceType,
            isAuthenticatedStatus);
        } else if (uuid) {
            // if logged in user clicked on the view details button, redirect to the order detail page
            const ordersURL = getCryptedOrderDetailsURL(orderNumber, uuid);
            history.push(ordersURL);
        }
    };

    renderTrackingLink = (carrier, trackingNumber, deliveryLink = '') => {
        let carrierUrl;
        switch (carrier) {
            case 'FEDEX':
                carrierUrl = FEDEX_ORDERTRACKING_URL;
                break;
            case 'UPS':
                carrierUrl = UPS_ORDERTRACKING_URL;
                break;
            case 'USPS':
            case 'MAIL':
                carrierUrl = USPS_ORDERTRACKING_URL;
                break;
            case 'UDS':
                carrierUrl = UDS_ORDERTRACKING_URL;
                break;
            default:
                carrierUrl = null;
        }
        if (!deliveryLink) {
            return carrierUrl ? (
                <a
                    href={carrierUrl + trackingNumber}
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                >
                    {trackingNumber}
                </a>
            ) : (
                <span>{trackingNumber}</span>
            );
        }
        return (
            <a
                href={deliveryLink}
                target="_blank"
                rel="noreferrer"
                className="link"
            >
                {trackingNumber}
            </a>
        );
    }

    showSubStatus = (ROSSItem = {}) => {
        const {
            statusDescription, history, type, deliveryLink,
        } = ROSSItem;
        if (type === 'FPT') {
            const isOrderDelayed = history.length > 0 && history[0].attemptedReason;
            if (isOrderDelayed) {
                const deliveryDescription = statusDescription || history[0].statusDescription;
                return (
                    <div className="delay-container">
                        <img src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blte21ce9e1faee603c/6042a5473c41f30bce48321a/Warning_Icon.svg" alt="warning" />
                        <b>{deliveryDescription}</b>
                    </div>
                );
            }
            // For mobile only, replaced `Delivered on June 11, 2021 at 01:00 PM` ==> `Delivered on June 11, 2021`
            const statusDescriptionMobile = statusDescription ? statusDescription.replace(/^(Delivered on[\s\S]+)(\sat\s\d\d:\d\d\s[AP]M)$/, '$1') : statusDescription;
            return <b>{statusDescriptionMobile}</b>;
        }

        if (type === 'GPT') {
            const isOrderDelayed = history.length > 0 && history[0].codeStatus === 'DE';
            if (isOrderDelayed) {
                const deliveryDescription = statusDescription || history[0].statusDescription;
                return (
                    <div className="delay-container">
                        <img src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blte21ce9e1faee603c/6042a5473c41f30bce48321a/Warning_Icon.svg" alt="warning" />
                        { deliveryLink  ? (
                            <a
                                href={deliveryLink}
                                target="_blank"
                                rel="noreferrer"
                                className="link"
                            >
                                {deliveryDescription}
                            </a>
                        ) : <b>{deliveryDescription}</b> }
                    </div>
                );
            }
            return deliveryLink ? (
                <a
                    href={deliveryLink}
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                >
                    {statusDescription}
                </a>
            ) : <b>{statusDescription}</b>;
        }
        return statusDescription ? (
            <b>{statusDescription}</b>
        ) : null;
    }

    showCarrier = (ROSSCarrier) => {
        if (ROSSCarrier === 'FEDEX') {
            return <img alt="fedex logo" src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blt986d6df1eca50df5/5f9715bf545bdb56ce4903e6/fedex-logo.svg" />;
        }
        if (ROSSCarrier === 'UDS') {
            return <img alt="uds logo" src="https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltc6883e40544a7af1/6165e3b4a481550b84bdc2eb/UDS_logo_1.svg" />;
        }
        return <span className="bold">{ROSSCarrier}</span>;
    }

    render() {
        const {
            order,
            className,
            userProfile,
            // brandID,
            isROSSUIEnabled,
            ROSSOrder,
            featureFlags,
        } = this.props;

        if (!userProfile) {
            return null;
        }

        const ROSSOrderPendingCancel = ROSSOrder?.pendingCancel;
        const IsPendingCancelEnabled = featureFlags?.['is-pending-cancel-enabled'];

        return isROSSUIEnabled && !isEmpty(ROSSOrder) ? (
            <div className={`orderListItem ${className} ${userProfile?.isAuthenticated ? '' : 'negative-margin'}`} style={{ padding: 0, borderTop: 0 }}>
                <div className="orderListHeader">
                    <div>Order #: {ROSSOrder.orderNumber}</div>
                    <Button
                        variant="contained"
                        className="buttonBlue"
                        onClick={() => this.handleViewDetailsClicked(
                            ROSSOrder.uuid,
                            ROSSOrder.orderNumber,
                            ROSSOrder.items[0].paymentStatus,
                            null,
                            ROSSOrder.trackingNumber,
                            ROSSOrder.items[0].carrier,
                        )}
                    >
                        {ROSSOrder.modifyOrder === 'true'
                            ? 'View Details / Modify'
                            : 'View Details'}
                    </Button>
                </div>
                {ROSSOrder.items.map((ROSSItem, i) => {
                    const passportRegex = /(passport annual membership|passport for|passport at)/gi;
                    const passPortLogo = 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltd8ac364c488b6313/5fd24cc550460c30dd5404bc/passport-logo.jpg';
                    const fallbackImgURL = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltc881f4b32ef5f484/60dcd2de4b10c259bbae6426/Image_Not_Available.png';
                    const ROSSProductImageURL = ROSSItem?.prdImgCtg && ROSSItem?.prdImgCtg !== '' ? secureProtocol(ROSSItem?.prdImgCtg) : fallbackImgURL;
                    const ROSSDeliveryDate = ROSSItem.estimatedDeliveryDate ? dateMMMMDDYYYY(ROSSItem.estimatedDeliveryDate) : dateMMMMDDYYYY(ROSSItem.deliveryDate);
                    // const ROSSFlexDeliveryDate = ROSSItem.flexDate ? dateMMMMDDYYYY(ROSSItem.flexDate) : '';
                    const ROSSDeliveryStatus = ROSSItem?.status;
                    const isPassportItem = ROSSItem.subscriptionType === 'Passport' || ROSSItem?.productName?.match(passportRegex)?.length > 0;
                    const ROSSRecip = `${ROSSItem?.recpFirstName} ${ROSSItem?.recpLastName}`;
                    const isSameLogicalOrder = ROSSOrder?.intOrderNumber === ROSSItem?.intOrderNumber;
                    const isCYOColChild = ROSSItem?.addonType === 'CYO' || ROSSItem?.addonType === 'COLLECTION';
                    const isDonationItem = Helper.checkIsDonationItem(ROSSItem?.productName);
                    const showDelivered = isPassportItem || isDonationItem;
                    // const itemType = ROSSItem.itemType;
                    return (
                        <React.Fragment key={['_', i].join('')}>
                            {(!isCYOColChild && (
                                <Grid container className="orderListProduct">
                                    <Grid className="orderInfo" item xs={9}>
                                        {(i === 0 || !isSameLogicalOrder) && (
                                            <>
                                                <div className="recipientInfo">
                                                    <div className="bold recipientName">
                                                        {ROSSRecip}
                                                    </div>
                                                    <div className="small uppercase">
                                                        {ROSSItem?.businessName
                                                            ? ROSSItem?.businessName
                                                            : ''}
                                                    </div>
                                                </div>
                                                <StatusTracking
                                                    ROSSStatus={showDelivered ? 'DELIVERED' : ROSSDeliveryStatus}
                                                    isROSSUIEnabled={isROSSUIEnabled}
                                                    ROSSOrderPendingCancel={ROSSOrderPendingCancel}
                                                    IsPendingCancelEnabled={IsPendingCancelEnabled}
                                                />
                                                <div className="subStatusContainer">
                                                    {this.showSubStatus(ROSSItem?.orderStatusAttributes)}
                                                </div>
                                                {/* Display order status information if it's not the cancelled, pending cancel and donation item */}
                                                {ROSSDeliveryStatus !== 'CANCELLED' && !ROSSOrderPendingCancel && !isDonationItem
                                                    ? (
                                                        <>
                                                            {isPassportItem
                                                                ? (
                                                                    <Grid className="orderDetail" item xs={12}>
                                                                        <div className="gray-color uppercase">Membership Start Date: </div>
                                                                        <span className="bold">{ROSSDeliveryDate}</span>
                                                                    </Grid>
                                                                ) : (
                                                                    <>
                                                                        {/* Hide Requested Date on mobile */}
                                                                        {/* {ROSSFlexDeliveryDate || ROSSDeliveryDate
                                                                        ? (
                                                                            <Grid className="orderDetail" item xs={12}>
                                                                                <span className="gray-color uppercase">{brandID === 'HD' ? 'Delivery Date / Arrives: ' : 'Requested Date: '}</span>
                                                                                <span className="bold">{ROSSFlexDeliveryDate || ROSSDeliveryDate}</span>
                                                                            </Grid>
                                                                        ) : null} */}
                                                                        {ROSSItem.itemType === 'FPT'
                                                                            ? (
                                                                                <>
                                                                                    <Grid className="orderDetail orderDetailMargin" item xs={12}>
                                                                                        <span className="gray-color uppercase">Delivered by: </span>
                                                                                        <span className="bold">Local Shop</span>
                                                                                    </Grid>
                                                                                    {ROSSItem.dtwName
                                                                                        ? (
                                                                                            <Grid className="orderDetail" item xs={12}>
                                                                                                <span className="gray-color uppercase">Requested time: </span>
                                                                                                <span className="bold">{ROSSItem.dtwName}</span>
                                                                                            </Grid>
                                                                                        ) : null }
                                                                                </>
                                                                            ) : null}
                                                                        {ROSSItem.itemType === 'GPT'
                                                                            ? (
                                                                                <>
                                                                                    {ROSSItem?.carrier && (
                                                                                        <Grid className="orderDetail orderDetailMargin display-flex" item xs={12}>
                                                                                            <span className="gray-color uppercase">Delivered by:&nbsp;</span>
                                                                                            {this.showCarrier(ROSSItem?.carrier)}
                                                                                        </Grid>
                                                                                    )}
                                                                                    {/* Won't display the Tracking # when it's delivered on mobile */}
                                                                                    {(ROSSDeliveryStatus !== 'DELIVERED') && (
                                                                                        <Grid className="orderDetail orderDetailMargin" item xs={12}>
                                                                                            <span className="gray-color uppercase">Tracking: </span>
                                                                                            <span className="bold">{ROSSItem?.trackingNumber
                                                                                                ? (
                                                                                                    this.renderTrackingLink(ROSSItem?.carrier, ROSSItem?.trackingNumber, ROSSItem?.orderStatusAttributes?.deliveryLink)
                                                                                                ) : 'Available once shipped'}
                                                                                            </span>
                                                                                        </Grid>
                                                                                    )}
                                                                                </>
                                                                            ) : null}
                                                                        <>
                                                                            <Grid className="orderDetail" item xs={12}>
                                                                                <span className="gray-color uppercase">BRAND: </span>
                                                                                <span className="bold">{brandNames[ROSSItem?.productBrand]}</span>
                                                                            </Grid>
                                                                        </>
                                                                    </>
                                                                )}
                                                        </>
                                                    ) : null}
                                            </>
                                        )}
                                    </Grid>
                                    <Grid className="productImg" item xs={3}>
                                        <img
                                            src={isPassportItem ? passPortLogo : ROSSProductImageURL}
                                            alt={ROSSItem.productSku}
                                            onError={(e) => {
                                                if (e.target.src !== fallbackImgURL) {
                                                    e.target.src = fallbackImgURL;
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                        </React.Fragment>
                    );
                })}
            </div>
        ) : (
            <div className={`orderListItem ${className}`}>
                <div className="align-right small margin-bottom">
                    Order #: {order?.orderNumber}
                </div>
                {order?.items.map((item, i) => {
                    const passportRegex = /(passport annual membership)/gi;
                    const isPassportItem = item.subscriptionType === 'Passport' || item.productName?.match(passportRegex)?.length > 0;
                    return (
                        <React.Fragment key={['_', i].join('')}>
                            <div className="orderListProduct">
                                <div className="split margin-bottom-2x">
                                    <div>
                                        <div className="bold">
                                            {item.recpFirstName} {item.recpLastName}
                                        </div>
                                        <div className="small uppercase">
                                            {item.businessName
                                                ? item.businessName
                                                : ''}
                                        </div>
                                    </div>
                                    <Button
                                        variant="contained"
                                        className="buttonBlue"
                                        onClick={() => this.handleViewDetailsClicked(
                                            order.uuid,
                                            order.orderNumber,
                                            item.paymentStatus,
                                            item.status,
                                            item.trackingNumber,
                                            item.carrier,
                                        )}
                                    >
                                        {item.modifyOrder === 'true'
                                            ? 'View Details / Modify'
                                            : 'View Details'}
                                    </Button>
                                </div>
                                <StatusTracking
                                    status={isPassportItem ? 'D' : item.status || item.paymentStatus}
                                />
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }
}

OrderListItem.defaultProps = {
    match: {},
    history: {},
    className: '',
    ROSSOrder: {},
    isROSSUIEnabled: false,
    featureFlags: {},
};

OrderListItem.propTypes = {
    order: object.isRequired,
    match: object,
    history: object,
    className: string,
    fetchOrderDetails: func.isRequired,
    userProfile: shape({
        isAuthenticated: bool.isRequired,
    }).isRequired,
    ROSSOrder: arrayOf({
        orderNumber: string,
        modifyOrder: bool,
        uuid: string,
        items: arrayOf(
            shape({
                itemType: string,
                prdImgCtg: string,
                productName: string,
                productSku: string,
                subscriptionType: string,
                paymentStatus: string,
                trackingNumber: string,
                carrier: string,
            }),
        ),
    }),
    isROSSUIEnabled: bool,
    featureFlags: object,
    ssrDeviceType: string.isRequired,
    // brandID: string.isRequired,
    isAuthenticatedStatus: bool.isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isAuthenticated: newState.isAuthenticated,
        ssrDeviceType: getSSRDeviceType(state),
        featureFlags: getFeatureFlags(state),
        brandID: getSiteConfig('brand-id'),
        isAuthenticatedStatus: getIsAuthenticatedStatus(state),
    };
};

const mapDispatchToProps = (dispatch) => ({
    fetchOrderDetails: (uuid, orderNumber, zipCode, billingZipCode, options, featureFlags, ssrDeviceType, isAuthenticatedStatus) => dispatch(EventDispatcher.fetchOrderDetails(
        uuid,
        orderNumber,
        zipCode,
        null,
        options,
        featureFlags,
        ssrDeviceType,
        isAuthenticatedStatus,
    )),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(OrderListItem));
